'use client'

import { ApolloLink, HttpLink } from '@apollo/client'
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
} from '@apollo/client-integration-nextjs'
import { CombinedGraphQLErrors, CombinedProtocolErrors } from '@apollo/client/errors'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { context, trace } from '@opentelemetry/api'
import { getPublicEnv } from '@tucan/utils'
import debug from 'debug'
import { createClient } from 'graphql-ws'
import { OperationTypeNode } from 'graphql/language/ast'
import { Kind } from 'graphql/language/kinds'
import WebSocket from 'isomorphic-ws'
import { PropsWithChildren } from 'react'
import { getNumber } from 'utils/get.number'
import { isBrowser } from 'utils/is.browser'
import { getGraphQLUrl } from '../../../graphql/get.graphql.url'
import { tokenRefreshLink } from '../../../graphql/token.link'

const logger = debug('gql')

function makeClient() {
  const httpLink = ApolloLink.from([
    new ErrorLink(({ error, operation }) => {
      if (CombinedGraphQLErrors.is(error)) {
        error.errors.forEach(({ message, locations, path }) =>
          logger(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
        )
      } else if (CombinedProtocolErrors.is(error)) {
        error.errors.forEach(({ message, extensions }) =>
          logger(`[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`)
        )
      } else {
        logger(`[Network error]: ${error}`)
      }
    }),
    tokenRefreshLink,
    new SetContextLink(async (ctx, request) => {
      const headers: any = {}

      if (isBrowser()) {
        if (ctx.recaptcha) {
          headers.recaptcha = ctx.recaptcha
        }
        headers['x-company-id'] = localStorage.getItem('company')
      } else {
        const span = trace.getSpan(context.active())
        if (span) {
          const { spanId, traceId } = span.spanContext()

          headers['X-B3-TraceId'] = traceId
          headers['X-B3-ParentSpanId'] = spanId
        }
      }

      if (ctx.Authorization) {
        headers['Authorization'] = ctx.Authorization
      }

      return {
        headers,
      }
    }),
    ApolloLink.split(
      ({ query }) => {
        const definition = getMainDefinition(query)
        return (
          definition.kind === Kind.OPERATION_DEFINITION &&
          definition.operation === OperationTypeNode.SUBSCRIPTION
        )
      },
      new GraphQLWsLink(
        createClient({
          connectionParams: () => {
            if (!isBrowser()) {
              return {}
            }

            return {
              isWebSocket: true,
            }
          },
          url: getGraphQLUrl({ websocket: true }),
          webSocketImpl: WebSocket,
        })
      ),
      ApolloLink.split(
        (op) => {
          if (getNumber(getPublicEnv('GRAPHQL_BATCH_INTERVAL')) === 0) {
            return false
          }

          const batch = op.getContext()['batch']

          return batch === undefined || batch === true
        },
        new BatchHttpLink({
          batchInterval: getNumber(getPublicEnv('GRAPHQL_BATCH_INTERVAL')),
          uri: getGraphQLUrl(),
        }),
        new HttpLink({
          uri: getGraphQLUrl(),
        })
      )
    ),
  ])

  return new ApolloClient({
    // use the `NextSSRInMemoryCache`, not the normal `InMemoryCache`
    cache: new InMemoryCache(),
    link: httpLink,
  })
}

// you need to create a component to wrap your app in
export function ApolloProvider({ children }: PropsWithChildren) {
  return <ApolloNextAppProvider makeClient={makeClient}>{children}</ApolloNextAppProvider>
}
